import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import CustomerHeader from '../components/CustomerHeader';
import CustomAlert from '../components/CustomAlert';
import { FaQrcode, FaCamera, FaBarcode, FaKeyboard, FaCheckCircle, FaExclamationCircle, FaSpinner } from 'react-icons/fa';
import { Html5Qrcode } from 'html5-qrcode';
import authService from '../services/authService';

const PageContainer = styled.div`
  min-height: 100vh;
  background: ${props => props.theme.background};
`;

const ContentContainer = styled.div`
  max-width: 600px;
  margin: 0 auto;
  padding: 1rem;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.primary};
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0.5rem 0;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s;

  &:hover {
    color: ${props => props.theme.primaryDark || '#c00000'};
  }

  &:active {
    transform: translateX(-2px);
  }
`;

const Title = styled.h2`
  color: ${props => props.theme.primary};
  font-size: 1.5rem;
  margin-bottom: 1.5rem;
  text-align: center;
`;

const ScannerSection = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const ScannerContainer = styled.div`
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
  border-radius: 12px;
  overflow: hidden;
  background: #000;
  min-height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
`;

const ScannerPlaceholder = styled.div`
  text-align: center;
  color: white;
  padding: 2rem;
`;

const ScannerIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 1rem;
  animation: pulse 2s infinite;

  @keyframes pulse {
    0% { opacity: 1; }
    50% { opacity: 0.5; }
    100% { opacity: 1; }
  }
`;

const ScannerOverlay = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 200px;
  height: 200px;
  border: 3px solid ${props => props.theme.primary};
  border-radius: 12px;
  pointer-events: none;
  
  &::before,
  &::after {
    content: '';
    position: absolute;
    width: 30px;
    height: 30px;
    border: 3px solid ${props => props.theme.primary};
  }
  
  &::before {
    top: -3px;
    left: -3px;
    border-right: none;
    border-bottom: none;
  }
  
  &::after {
    bottom: -3px;
    right: -3px;
    border-left: none;
    border-top: none;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' | 'success' }>`
  flex: 1;
  padding: 0.75rem;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  
  ${props => {
    if (props.variant === 'secondary') {
      return `
        background: white;
        color: ${props.theme.textPrimary};
        border: 2px solid ${props.theme.border};
        
        &:hover {
          background: ${props.theme.surface};
        }
      `;
    } else if (props.variant === 'success') {
      return `
        background: #4caf50;
        color: white;
        border: none;
        
        &:hover {
          background: #45a049;
        }
      `;
    }
    return `
      background: ${props.theme.primary};
      color: white;
      border: none;
      
      &:hover {
        background: ${props.theme.primaryDark};
      }
    `;
  }}
`;

const ManualEntrySection = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const SectionTitle = styled.h3`
  color: ${props => props.theme.textPrimary};
  font-size: 1.1rem;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const InputGroup = styled.div`
  margin-bottom: 1rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  color: ${props => props.theme.textPrimary};
  font-size: 0.9rem;
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid ${props => props.theme.border};
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.3s;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.primary};
  }
`;

const ResultSection = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'isVisible'
})<{ isVisible: boolean }>`
  display: ${props => props.isVisible ? 'block' : 'none'};
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const ResultHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
  color: #4caf50;
  font-size: 1.1rem;
  font-weight: 600;
`;

const ResultInfo = styled.div`
  background: ${props => props.theme.surface};
  border-radius: 8px;
  padding: 1rem;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const InfoLabel = styled.span`
  color: ${props => props.theme.textSecondary};
  font-size: 0.85rem;
`;

const InfoValue = styled.span`
  color: ${props => props.theme.textPrimary};
  font-size: 0.85rem;
  font-weight: 600;
`;

const InstructionText = styled.p`
  color: ${props => props.theme.textSecondary};
  font-size: 0.85rem;
  text-align: center;
  margin: 1rem 0;
  line-height: 1.5;
`;

const TabContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  background: ${props => props.theme.surface};
  padding: 0.25rem;
  border-radius: 8px;
`;

const Tab = styled.button<{ active?: boolean }>`
  flex: 1;
  padding: 0.75rem;
  border-radius: 6px;
  border: none;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  
  ${props => props.active ? `
    background: white;
    color: ${props.theme.primary};
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  ` : `
    background: transparent;
    color: ${props.theme.textSecondary};
    
    &:hover {
      background: rgba(255, 255, 255, 0.5);
    }
  `}
`;

const ScanGreenTag: React.FC = () => {
  const navigate = useNavigate();
  const [scanMode, setScanMode] = useState<'scan' | 'manual'>('scan');
  const [isScanning, setIsScanning] = useState(false);
  const [scanResult, setScanResult] = useState<any>(null);
  const [manualInput, setManualInput] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [html5QrCode, setHtml5QrCode] = useState<Html5Qrcode | null>(null);
  
  const [alert, setAlert] = useState<{
    isVisible: boolean;
    type?: 'success' | 'error' | 'warning' | 'info' | 'confirm';
    title?: string;
    message: string;
    onConfirm?: () => void;
  }>({ isVisible: false, message: '' });

  useEffect(() => {
    return () => {
      if (html5QrCode) {
        html5QrCode.stop().catch(err => console.error('Failed to stop scanner:', err));
      }
    };
  }, [html5QrCode]);

  const startScanning = async () => {
    try {
      const scanner = new Html5Qrcode("qr-reader");
      setHtml5QrCode(scanner);
      
      await scanner.start(
        { facingMode: "environment" },
        {
          fps: 10,
          qrbox: { width: 250, height: 250 }
        },
        (decodedText) => {
          handleScanSuccess(decodedText);
          scanner.stop();
          setIsScanning(false);
        },
        (errorMessage) => {
          // Ignore errors while scanning
        }
      );
      
      setIsScanning(true);
    } catch (err) {
      setAlert({
        isVisible: true,
        type: 'error',
        title: 'Camera Error',
        message: 'Unable to access camera. Please check permissions or use manual entry.'
      });
    }
  };

  const stopScanning = () => {
    if (html5QrCode) {
      html5QrCode.stop().then(() => {
        setIsScanning(false);
      }).catch(err => {
        console.error('Failed to stop scanner:', err);
      });
    }
  };

  const handleScanSuccess = async (result: string) => {
    setIsValidating(true);

    try {
      const response = await authService.validateGreenTag(result);

      if (response.success) {
        // Store the green tag number
        localStorage.setItem('greenTagNumber', result);

        // Set scan result for display
        setScanResult({
          greenTag: result,
          validated: true,
          userType: authService.getUserType(),
          customerData: authService.getCustomerData(),
          salesData: authService.getSalesData()
        });

        setAlert({
          isVisible: true,
          type: 'success',
          title: 'Authentication Successful',
          message: `Green Tag ${result} has been successfully validated. You are now logged in.`
        });

        // Navigate to dashboard after successful authentication
        setTimeout(() => {
          navigate('/dashboard');
        }, 2000);
      } else {
        throw new Error(response.message || 'Green Tag validation failed');
      }
    } catch (error: any) {
      console.error('Green tag validation failed:', error);

      setAlert({
        isVisible: true,
        type: 'error',
        title: 'Validation Failed',
        message: error.message || 'Failed to validate Green Tag. Please try again or contact support.'
      });
    } finally {
      setIsValidating(false);
    }
  };

  const handleManualSubmit = () => {
    if (!manualInput.trim()) {
      setAlert({
        isVisible: true,
        type: 'error',
        title: 'Validation Error',
        message: 'Please enter a Green Tag or Serial Number'
      });
      return;
    }

    handleScanSuccess(manualInput);
  };


  return (
    <PageContainer>
      <CustomerHeader skipAuthCheck={true} />
      <ContentContainer>
        <BackButton onClick={() => navigate('/dashboard')}>
          ← Back
        </BackButton>
        <Title>Scan Green Tag / Serial No.</Title>
        
        <ScannerSection>
          <TabContainer>
            <Tab 
              active={scanMode === 'scan'}
              onClick={() => setScanMode('scan')}
            >
              {React.createElement(FaCamera as any, {})} Camera Scan
            </Tab>
            <Tab 
              active={scanMode === 'manual'}
              onClick={() => setScanMode('manual')}
            >
              {React.createElement(FaKeyboard as any, {})} Manual Entry
            </Tab>
          </TabContainer>

          {scanMode === 'scan' ? (
            <>
              <ScannerContainer id="qr-reader">
                {!isScanning && (
                  <ScannerPlaceholder>
                    <ScannerIcon>
                      {React.createElement(FaQrcode as any, {})}
                    </ScannerIcon>
                    <p>Click below to start scanning</p>
                  </ScannerPlaceholder>
                )}
                {isScanning && <ScannerOverlay />}
              </ScannerContainer>
              
              <InstructionText>
                Position the QR code or barcode within the frame to scan automatically
              </InstructionText>
              
              <ButtonGroup>
                {!isScanning ? (
                  <Button variant="primary" onClick={startScanning} disabled={isValidating}>
                    {isValidating ? (
                      <>
                        {React.createElement(FaSpinner as any, { style: { animation: 'spin 1s linear infinite' } })} Validating...
                      </>
                    ) : (
                      <>
                        {React.createElement(FaCamera as any, {})} Start Scanning
                      </>
                    )}
                  </Button>
                ) : (
                  <Button variant="secondary" onClick={stopScanning} disabled={isValidating}>
                    {isValidating ? 'Validating...' : 'Stop Scanning'}
                  </Button>
                )}
              </ButtonGroup>
            </>
          ) : (
            <ManualEntrySection>
              <SectionTitle>
                {React.createElement(FaBarcode as any, {})}
                Manual Entry
              </SectionTitle>
              
              <InputGroup>
                <Label>Green Tag / Serial Number</Label>
                <Input
                  type="text"
                  placeholder="Enter Green Tag or Serial Number"
                  value={manualInput}
                  onChange={(e) => setManualInput(e.target.value)}
                />
              </InputGroup>
              
              <Button variant="primary" onClick={handleManualSubmit} disabled={isValidating}>
                {isValidating ? (
                  <>
                    {React.createElement(FaSpinner as any, { style: { animation: 'spin 1s linear infinite' } })} Validating...
                  </>
                ) : (
                  <>
                    {React.createElement(FaCheckCircle as any, {})} Verify Tag
                  </>
                )}
              </Button>
            </ManualEntrySection>
          )}
        </ScannerSection>

        <ResultSection isVisible={!!scanResult && scanResult.validated}>
          <ResultHeader>
            {React.createElement(FaCheckCircle as any, {})}
            Authentication Successful
          </ResultHeader>

          <ResultInfo>
            <InfoRow>
              <InfoLabel>Green Tag:</InfoLabel>
              <InfoValue>{scanResult?.greenTag}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>User Type:</InfoLabel>
              <InfoValue style={{ color: '#4caf50', textTransform: 'capitalize' }}>{scanResult?.userType}</InfoValue>
            </InfoRow>
            {scanResult?.userType === 'customer' && scanResult?.customerData && (
              <>
                <InfoRow>
                  <InfoLabel>Customer Name:</InfoLabel>
                  <InfoValue>{scanResult.customerData.name}</InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel>Customer Number:</InfoLabel>
                  <InfoValue>{scanResult.customerData.number}</InfoValue>
                </InfoRow>
                {scanResult.customerData.address && (
                  <InfoRow>
                    <InfoLabel>Address:</InfoLabel>
                    <InfoValue>{scanResult.customerData.address}</InfoValue>
                  </InfoRow>
                )}
              </>
            )}
            {scanResult?.userType === 'sales' && scanResult?.salesData && (
              <>
                <InfoRow>
                  <InfoLabel>Sales Rep:</InfoLabel>
                  <InfoValue>{scanResult.salesData.name}</InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel>Employee No:</InfoLabel>
                  <InfoValue>{scanResult.salesData.empNo}</InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel>Role:</InfoLabel>
                  <InfoValue>{scanResult.salesData.roleName}</InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel>Organization:</InfoLabel>
                  <InfoValue>{scanResult.salesData.orgName}</InfoValue>
                </InfoRow>
              </>
            )}
          </ResultInfo>

          <ButtonGroup style={{ marginTop: '1rem' }}>
            <Button variant="secondary" onClick={() => setScanResult(null)}>
              Scan Another
            </Button>
            <Button variant="success" onClick={() => navigate('/dashboard')}>
              Go to Dashboard
            </Button>
          </ButtonGroup>
        </ResultSection>
      </ContentContainer>

      <CustomAlert
        isVisible={alert.isVisible}
        type={alert.type}
        title={alert.title}
        message={alert.message}
        onClose={() => setAlert({ isVisible: false, message: '' })}
        onConfirm={alert.onConfirm}
      />
    </PageContainer>
  );
};

export default ScanGreenTag;